import React, { useEffect, useState } from 'react';
import { ScaleSliderExpanded, RotateSliderExpanded } from './SliderControls';
import { DARK_THEME_CSS, LAYOUT_CONFIG, FONT_CONFIG, COLOR_CONFIG } from '../../../constants';
import { SCALE_CONSTANTS } from '../../../js/text/scaleUtils';
import './MobileToolbarBottomSheet.css';
import { setShowText } from '../../../redux-tool/slice/controlSlice';
import { useDispatch } from 'react-redux';

// Helper function to normalize font-family string to configured font "name"
const getFontName = (fontFamily) => {
  if (!fontFamily) return '';

  const trimmed = fontFamily.trim();

  // If it's already one of our configured names, return it directly
  const directMatch = FONT_CONFIG.find((f) => f.name === trimmed);
  if (directMatch) return directMatch.name;

  // If it matches a configured family exactly, map back to its name
  const exactFamilyMatch = FONT_CONFIG.find((f) => f.family === trimmed);
  if (exactFamilyMatch) return exactFamilyMatch.name;

  // Work with the first font token only and strip any surrounding quotes
  const base = (trimmed.includes(',') ? trimmed.split(',')[0] : trimmed)
    .trim()
    .replace(/^['"]|['"]$/g, '');

  // Try to match by comparing the first token of each configured family
  const tokenMatch = FONT_CONFIG.find((f) => {
    const configBase = (f.family.includes(',') ? f.family.split(',')[0] : f.family)
      .trim()
      .replace(/^['"]|['"]$/g, '');
    return configBase === base;
  });

  if (tokenMatch) return tokenMatch.name;

  // Fallback to the cleaned base token
  return base;
};

const MobileFloatingToolbar = ({
  isMobile,
  hasActiveText,
  activePanel,
  setActivePanel,
  scale,
  rotate,
  handleScaleChange,
  handleRotateChange,
  handleColorChange,
  handleFontChange,
  handleDeleteText,
  focusInput,
  handleNewText,
  color,
  fontFamily,
  textList,
  activeTextId,
  findTextByIdMemo,
  setShouldMaintainFocus,
  setShowFloatingInput,
  deselectText,
  text,
  setText,
  restoreTextSelection,
}) => {
  const dispatch = useDispatch();
  // Track last active panel to maintain selected state after closing bottom sheet
  const [lastActivePanel, setLastActivePanel] = useState(null);

  // Update lastActivePanel when activePanel changes
  useEffect(() => {
    if (isMobile && activePanel && ['color', 'font', 'scale', 'rotate'].includes(activePanel)) {
      setLastActivePanel(activePanel);
    } else if (!hasActiveText) {
      // Reset lastActivePanel when no text is selected
      setLastActivePanel(null);
    }
  }, [isMobile, activePanel, hasActiveText]);

  // Manage body scroll when bottom sheet is open on mobile
  useEffect(() => {
    if (isMobile && activePanel && ['color', 'font', 'scale', 'rotate'].includes(activePanel)) {
      document.body.classList.add('bottom-sheet-open');
    } else {
      document.body.classList.remove('bottom-sheet-open');
    }

    return () => {
      document.body.classList.remove('bottom-sheet-open');
    };
  }, [isMobile, activePanel]);

  // Helper function to render slider in bottom sheet
  const renderSliderInBottomSheet = (value, onChange, min, max, step, labelMin, labelMax) => {
    const percentage = ((value - min) / (max - min)) * 100;

    return (
      <div className="slider-container">
        <div className="slider-wrapper">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="slider-input"
            style={{
              background: `linear-gradient(to right, #40E0FF 0%, #40E0FF ${percentage}%, rgba(64, 224, 255, 0.3) ${percentage}%, rgba(64, 224, 255, 0.3) 100%)`,
            }}
            onInput={(e) => {
              const val = parseFloat(e.target.value);
              const percent = ((val - min) / (max - min)) * 100;
              e.target.style.background = `linear-gradient(to right, #40E0FF 0%, #40E0FF ${percent}%, rgba(64, 224, 255, 0.3) ${percent}%, rgba(64, 224, 255, 0.3) 100%)`;
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          /* Dark theme styles */
          ${DARK_THEME_CSS}
          
          /* Loading spinner animation */
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .color-preview:hover .color-tooltip {
            opacity: 1 !important;
          }
          
          /* Ensure proper cursor behavior */
          button, [role="button"], .clickable {
            cursor: pointer !important;
          }
          
          /* Slider cursor improvements */
          .MuiSlider-root {
            cursor: pointer !important;
          }
          
          .MuiSlider-thumb {
            cursor: grab !important;
          }
          
          .MuiSlider-thumb:active {
            cursor: grabbing !important;
          }
          
          /* Color swatch cursor improvements */
          .color-swatch {
            cursor: pointer !important;
          }
          
          /* Font button cursor improvements */
          .font-button {
            cursor: pointer !important;
            position: relative !important;
            z-index: 10 !important;
            pointer-events: auto !important;
          }
          
          /* Ensure font buttons are not blocked */
          .font-button * {
            pointer-events: auto !important;
          }
          
          /* Ensure sliders don't overflow their containers */
          .MuiSlider-root {
            max-width: 100% !important;
            overflow: visible !important;
          }
          
          /* Responsive design styles */
          @media (max-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT - 1}px) {
            /* Mobile-specific styles */
            
            /* Enhanced touch targets */
            button, [role="button"], .clickable {
              min-height: ${LAYOUT_CONFIG.MIN_TOUCH_TARGET}px;
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
            }
            
            /* Prevent zoom on input focus */
            input, textarea {
              font-size: 16px !important;
            }
          }
          
          /* Very narrow screen optimizations */
          @media (max-width: ${LAYOUT_CONFIG.MIN_WIDTH}px) {
            .text-editor-container {
              padding: 0;
            }
          }
          
          /* Small height optimizations */
          @media (max-height: 600px) {
            .text-elements-list {
              max-height: 200px;
              overflow-y: auto;
            }
          }
          
          /* Mobile text list positioning */
          @media (max-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT - 1}px) {
            .text-elements-list {
              top: 5px !important;
              right: 5px !important;
              width: calc(100vw - 20px) !important;
              max-width: 320px !important;
            }
          }
          
          /* Modern Floating Toolbar Styles - Match Image Design */
          .floating-toolbar {
            position: relative;
          }
          
          .toolbar-icon {
            position: relative;
            overflow: hidden;
          }
          
          .toolbar-icon:not(.add-btn)::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(45deg, transparent, rgba(64, 224, 255, 0.1), transparent);
            transform: translateX(-100%);
            transition: transform 0.6s ease;
          }
          
          .toolbar-icon:hover::before {
            transform: translateX(100%);
          }
          
          .toolbar-icon:active {
            transform: scale(0.95);
          }
          
          /* Icon States - Match Design */
          .toolbar-icon {
            position: relative;
            overflow: hidden;
          }
          
          .toolbar-icon.add-btn {
            background: transparent;
            border: none;
            color: #FFFFFF;
          }
          
          /* Selected state - cyan background */
          .toolbar-icon.selected {
           background: rgba(64, 224, 255, 0.1) !important;
           border: 2px solid rgba(64, 224, 255, 0.3) !important;
            transform: scale(1.05);
          }
          
          /* Delete button special styling */
          .delete-btn:hover:not(:disabled) {
            background: rgba(255, 68, 68, 0.2) !important;
            border-color: rgba(255, 68, 68, 0.5) !important;
            box-shadow: 0 0 15px rgba(255, 68, 68, 0.3) !important;
          }
          
          /* Color Palette Expanded Styles */
          .color-palette-expanded {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: rgba(20, 25, 35, 0.98);
            backdropFilter: blur(20px);
            WebkitBackdropFilter: blur(20px);
            borderRadius: 0 0 10px 10px;
            padding: 10px;
            border: 1px solid rgba(64, 224, 255, 0.2);
            borderTop: none;
            boxShadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            zIndex: 10;
            maxHeight: 120px;
            overflow: hidden;
          }
          
          .color-circle {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
          }
          
          .color-circle:hover {
            transform: scale(1.1) !important;
            box-shadow: 0 0 20px rgba(64, 224, 255, 0.4) !important;
          }
          
          /* Font Pill Hover Effects */
          .font-pill:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 4px 12px rgba(64, 224, 255, 0.3) !important;
          }
          
          /* Font List Scrollbar Styles */
          .font-list-container::-webkit-scrollbar {
            height: 6px;
          }
          
          .font-list-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 3px;
            margin: 0 2px; /* Add margin to prevent overlay */
          }
          
          .font-list-container::-webkit-scrollbar-thumb {
            background: rgba(64, 224, 255, 0.3);
            border-radius: 3px;
          }
          
          .font-list-container::-webkit-scrollbar-thumb:hover {
            background: rgba(64, 224, 255, 0.5);
          }
          
          /* Color Palette Scrollbar Styles */
          .color-palette-container::-webkit-scrollbar {
            height: 6px;
          }
          
          .color-palette-container::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 3px;
            margin: 0 2px; /* Add margin to prevent overlay */
          }
          
          .color-palette-container::-webkit-scrollbar-thumb {
            background: rgba(64, 224, 255, 0.3);
            border-radius: 3px;
          }
          
          .color-palette-container::-webkit-scrollbar-thumb:hover {
            background: rgba(64, 224, 255, 0.5);
          }
          
          /* Firefox scrollbar */
          .font-list-container {
            scrollbar-width: thin;
            scrollbar-color: rgba(64, 224, 255, 0.3) transparent;
            /* Ensure scrollbar doesn't overlay content */
            box-sizing: border-box;
          }
          
          .color-palette-container {
            scrollbar-width: thin;
            scrollbar-color: rgba(64, 224, 255, 0.3) transparent;
            /* Ensure scrollbar doesn't overlay content */
            box-sizing: border-box;
          }
          
          /* Responsive adjustments */
          @media (max-width: 480px) {
            .floating-toolbar {
              height: 65px !important;
              max-height: 65px !important;
              padding: 2px 5px !important;
              margin: 0px !important;
            }
            
            .toolbar-icon {
              width: 40px !important;
              height: 40px !important;
              font-size: 16px !important;
            }
            
            .toolbar-icon.font-btn {
              font-size: 14px !important;
            }
            
            /* Color palette adjustments for small screens */
            .color-palette-expanded {
              maxHeight: 100px !important;
              padding: 0px !important;
              padding-top: 3px !important;
            }
            
            .color-palette-container {
              gap: 6px !important;
              padding: 1px 1px 6px 1px !important;
            }
          }
        `}
      </style>

      {/* Modern Floating Toolbar - Match Image Design */}
      <div
        className="floating-toolbar"
        style={{
          height: isMobile ? '60px' : '70px',
          maxHeight: isMobile ? '60px' : '70px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '8px 16px',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          position: 'relative',
          margin: '16px',
          width: '100%',
        }}
        onError={(error) => {
          console.error('TextEditor container error:', error);
        }}
      >
        {/* Left Section - Main Tools */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#292929',
            flex: '0 !important',
            borderRadius: '35px',
            border: '1px solid rgba(64, 224, 255, 0.2)',
            boxShadow: '0 2px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(64, 224, 255, 0.1)',
            boxSizing: 'border-box',
            padding: '5px 10px',
          }}
        >
          {/* Color Picker Icon - SVG */}
          <button
            className={`toolbar-icon color-picker-btn ${
              (isMobile ? lastActivePanel === 'color' : activePanel === 'color') ? 'selected' : ''
            }`}
            onClick={() => {
              if (hasActiveText) {
                setActivePanel(activePanel === 'color' ? null : 'color');
              }
            }}
            disabled={!hasActiveText}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              position: 'relative',
              overflow: 'hidden',
              opacity: hasActiveText ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(64, 224, 255, 0.5)';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <img
              src="/theme/img/action-icon/color.svg"
              alt="Color"
              style={{ width: '24px', height: '24px' }}
            />
          </button>

          {/* Font Selection Icon - SVG */}
          <button
            className={`toolbar-icon font-btn ${
              (isMobile ? lastActivePanel === 'font' : activePanel === 'font') ? 'selected' : ''
            }`}
            onClick={() => {
              if (hasActiveText) {
                setActivePanel(activePanel === 'font' ? null : 'font');
              }
            }}
            disabled={!hasActiveText}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: hasActiveText
                ? activePanel === 'font'
                  ? '2px solid rgba(150, 150, 150, 0.5)'
                  : '2px solid rgba(255, 255, 255, 0.2)'
                : '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              position: 'relative',
              opacity: hasActiveText ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (hasActiveText && activePanel !== 'font') {
                e.target.style.borderColor = 'rgba(64, 224, 255, 0.5)';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasActiveText && activePanel !== 'font') {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <img
              src="/theme/img/action-icon/font.svg"
              alt="Font"
              style={{ width: '24px', height: '18px' }}
            />
          </button>

          {/* Scale Icon - SVG */}
          <button
            className={`toolbar-icon scale-btn ${
              (isMobile ? lastActivePanel === 'scale' : activePanel === 'scale') ? 'selected' : ''
            }`}
            onClick={() => {
              if (hasActiveText) {
                setActivePanel(activePanel === 'scale' ? null : 'scale');
              }
            }}
            disabled={!hasActiveText}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              position: 'relative',
              opacity: hasActiveText ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(64, 224, 255, 0.5)';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <img
              src="/theme/img/action-icon/scale.svg"
              alt="Scale"
              style={{ width: '24px', height: '18px' }}
            />
          </button>

          {/* Rotate Icon - SVG */}
          <button
            className={`toolbar-icon rotate-btn ${
              (isMobile ? lastActivePanel === 'rotate' : activePanel === 'rotate') ? 'selected' : ''
            }`}
            onClick={() => {
              if (hasActiveText) {
                setActivePanel(activePanel === 'rotate' ? null : 'rotate');
              }
            }}
            disabled={!hasActiveText}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              position: 'relative',
              opacity: hasActiveText ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(64, 224, 255, 0.5)';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <img
              src="/theme/img/action-icon/rotate.svg"
              alt="Rotate"
              style={{ width: '24px', height: '18px' }}
            />
          </button>

          {/* Edit/Pencil Icon - SVG */}
          <button
            className="toolbar-icon edit-btn"
            onClick={() => {
              if (!hasActiveText && restoreTextSelection) {
                const restored = restoreTextSelection();
                if (restored) {
                  const currentText = findTextByIdMemo(activeTextId);
                  if (currentText) {
                    setText(currentText.Text || '');
                    setShowFloatingInput(true);
                  }
                }
              }

              if (hasActiveText && activeTextId) {
                // Get current text from active text
                const currentText = findTextByIdMemo(activeTextId);
                if (currentText) {
                  // Load current text into input
                  setText(currentText.Text || '');
                  // Show mobile modal for editing
                  setShowFloatingInput(true);
                }
              }
            }}
            disabled={!hasActiveText && !restoreTextSelection}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText || restoreTextSelection ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: hasActiveText || restoreTextSelection ? 1 : 0.5,
            }}
            // onMouseEnter={(e) => {
            //   if (hasActiveText || restoreTextSelection) {
            //     e.target.style.borderColor = 'rgba(64, 224, 255, 0.5)';
            //     e.target.style.transform = 'scale(1.05)';
            //   }
            // }}
            // onMouseLeave={(e) => {
            //   if (hasActiveText || restoreTextSelection) {
            //     e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            //     e.target.style.transform = 'scale(1)';
            //   }
            // }}
          >
            <img
              src="/theme/img/action-icon/edit.svg"
              alt="Edit"
              style={{ width: '24px', height: '18px' }}
            />
          </button>

          {/* Delete Icon - SVG */}
          <button
            className="toolbar-icon delete-btn"
            onClick={() => {
              if (hasActiveText && activeTextId) {
                dispatch(setShowText(false));

                setLastActivePanel(null);
                setActivePanel(null);
                handleDeleteText(activeTextId);
                deselectText(); // Clear selection after delete
              }
            }}
            disabled={!hasActiveText}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasActiveText ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: hasActiveText ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(255, 68, 68, 0.5)';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (hasActiveText) {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <img
              src="/theme/img/action-icon/delete.svg"
              alt="Delete"
              style={{ width: '24px', height: '18px' }}
            />
          </button>
        </div>

        {/* Right Section - Action Buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {/* Add New Text Button - SVG */}
          <button
            className="toolbar-icon add-btn"
            onClick={handleNewText}
            style={{
              background: 'transparent !important',
              border: 'none !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              padding: '0',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            <img
              src="/theme/img/action-icon/new.svg"
              alt="Add"
              style={{ width: '40px', height: '40px' }}
            />
          </button>
        </div>

        {/* Color Palette Expanded - Desktop (absolute) / Mobile (bottom sheet) */}
        {activePanel === 'color' && hasActiveText && !isMobile && (
          <div
            className="color-palette-expanded"
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              background: 'rgba(20, 25, 35, 0.98)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '0 0 10px 10px',
              padding: '10px',
              border: 'none',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              zIndex: 10,
              maxHeight: '120px',
              overflow: 'hidden',
            }}
          >
            <div
              className="color-palette-container"
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                padding: '2px 2px 8px 2px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(64, 224, 255, 0.3) transparent',
                height: 'auto',
                maxHeight: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {COLOR_CONFIG.map((colorOption, index) => (
                <div
                  key={index}
                  className="color-circle"
                  onClick={() => handleColorChange(colorOption.value)}
                  style={{
                    width: '26px',
                    height: '26px',
                    minWidth: '26px',
                    borderRadius: '50%',
                    backgroundColor: colorOption.value,
                    border:
                      color === colorOption.value
                        ? '1px solid #40E0FF'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Font Selection Expanded - Desktop (absolute) / Mobile (bottom sheet) */}
        {activePanel === 'font' && hasActiveText && !isMobile && (
          <div
            className="font-selection-expanded"
            style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              background: 'rgba(20, 25, 35, 0.98)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '0 0 10px 10px',
              padding: '2px 10px',
              border: '1px solid rgba(64, 224, 255, 0.2)',
              borderTop: 'none',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              zIndex: 10,
            }}
          >
            <div
              className="font-list-container"
              style={{
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                overflowY: 'hidden',
                padding: '2px 2px 8px 2px',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(64, 224, 255, 0.3) transparent',
                height: 'auto',
                maxHeight: 'none',
              }}
            >
              {FONT_CONFIG.map((fontOption, index) => {
                const isSelected = getFontName(fontFamily) === fontOption.name;
                return (
                  <button
                    key={index}
                    className="font-pill"
                    onClick={() => handleFontChange(fontOption.family)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '10px',
                      background: isSelected
                        ? 'linear-gradient(135deg, rgba(64, 224, 255, 0.2) 0%, rgba(53, 49, 49, 0.1) 100%)'
                        : 'rgba(53, 49, 49, 0.1)',
                      border: isSelected
                        ? '1px solid #40E0FF'
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      color: isSelected ? '#40E0FF' : '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '10px',
                      fontWeight: '500',
                      fontFamily: fontOption.family,
                      boxShadow: isSelected
                        ? '0 0 15px rgba(64, 224, 255, 0.4)'
                        : '0 2px 8px rgba(0, 0, 0, 0.2)',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                      minWidth: '70px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = 'rgba(64, 224, 255, 0.4)';
                        e.target.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {fontOption.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Scale Slider Expanded - Desktop (absolute) */}
        {activePanel === 'scale' && !isMobile && (
          <ScaleSliderExpanded scale={scale} handleScaleChange={handleScaleChange} />
        )}

        {/* Rotate Slider Expanded - Desktop (absolute) */}
        {activePanel === 'rotate' && !isMobile && (
          <RotateSliderExpanded rotate={rotate} handleRotateChange={handleRotateChange} />
        )}

        {/* Mobile Bottom Sheets */}
        {/* Color Palette Bottom Sheet */}
        {isMobile && activePanel === 'color' && hasActiveText && (
          <div className={`mobile-toolbar-bottom-sheet ${activePanel === 'color' ? 'show' : ''}`}>
            <div className="mobile-toolbar-bottom-sheet-header">
              <button
                className="mobile-toolbar-bottom-sheet-close"
                onClick={() => setActivePanel(null)}
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block' }}
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mobile-toolbar-bottom-sheet-content">
              <div className="color-palette-container">
                {COLOR_CONFIG.map((colorOption, index) => (
                  <div
                    key={index}
                    className="color-circle"
                    onClick={() => handleColorChange(colorOption.value)}
                    style={{
                      backgroundColor: colorOption.value,
                      border:
                        color === colorOption.value
                          ? '1px solid #40E0FF'
                          : '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Font Selection Bottom Sheet */}
        {isMobile && activePanel === 'font' && hasActiveText && (
          <div className={`mobile-toolbar-bottom-sheet ${activePanel === 'font' ? 'show' : ''}`}>
            <div className="mobile-toolbar-bottom-sheet-header">
              <button
                className="mobile-toolbar-bottom-sheet-close"
                onClick={() => setActivePanel(null)}
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block' }}
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mobile-toolbar-bottom-sheet-content">
              <div className="font-list-container">
                {FONT_CONFIG.map((fontOption, index) => {
                  const isSelected = getFontName(fontFamily) === fontOption.name;
                  return (
                    <button
                      key={index}
                      className={`font-pill ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleFontChange(fontOption.family)}
                      style={{
                        fontFamily: fontOption.family,
                      }}
                    >
                      {fontOption.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Scale Slider Bottom Sheet */}
        {isMobile && activePanel === 'scale' && (
          <div className={`mobile-toolbar-bottom-sheet ${activePanel === 'scale' ? 'show' : ''}`}>
            <div className="mobile-toolbar-bottom-sheet-header">
              <button
                className="mobile-toolbar-bottom-sheet-close"
                onClick={() => setActivePanel(null)}
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block' }}
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mobile-toolbar-bottom-sheet-content">
              {renderSliderInBottomSheet(
                scale,
                handleScaleChange,
                SCALE_CONSTANTS.SLIDER_MIN,
                SCALE_CONSTANTS.SLIDER_MAX,
                SCALE_CONSTANTS.SLIDER_STEP,
                `${SCALE_CONSTANTS.SLIDER_MIN}%`,
                `${SCALE_CONSTANTS.SLIDER_MAX}%`,
              )}
            </div>
          </div>
        )}

        {/* Rotate Slider Bottom Sheet */}
        {isMobile && activePanel === 'rotate' && (
          <div className={`mobile-toolbar-bottom-sheet ${activePanel === 'rotate' ? 'show' : ''}`}>
            <div className="mobile-toolbar-bottom-sheet-header">
              <button
                className="mobile-toolbar-bottom-sheet-close"
                onClick={() => setActivePanel(null)}
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block' }}
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="mobile-toolbar-bottom-sheet-content">
              {renderSliderInBottomSheet(rotate, handleRotateChange, -180, 180, 1, '0°', '360°')}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MobileFloatingToolbar;
